// Prezentační vrstva vyhledávání (issue #74, screens/17-vyhledavani.md).
//
// Buildery „data → DOM s `bs-*` třídami". NEČTOU nativní DOM a NEVOLAJÍ API —
// dostanou model a vrátí prvky. Díky tomu je krmí obě cesty:
//
//   searchNative.js  (nativní DOM → model)  ─┐
//                                            ├→ searchRender.js → bs-* DOM
//   searchApi.js     (Typesense → model)   ─┘        (jedna sada SCSS)
//
// Model (tvar sladěný s API kontraktem #66, `docs/ops/vyhledavani-koncepce.md`
// §API kontrakt), aby adaptér nativu nemusel překládat dvakrát:
//
//   {
//     query:    string,
//     products: { shown, total, hasMore },   // total === null = celek neznámý
//     sections: [ { kind, label, count, fold?, items: [{ name, url, count, found? }] } ],
//     chips:    [ { name, url, count } ]     // kategorie výsledku (jen API, 2026-10-14)
//   }
//
// `kind` ∈ cats | brands | posts | pages | products. `found` = počet u položky
// je počet shod s dotazem, ne velikost kategorie; `fold` = sekce se sbalí na
// prvních pár řádků se „Zobrazit vše (N)".
//
// POZOR na `total === null`: nativní Shoptet celkový počet nalezených produktů
// nikde nevystavuje (viz searchNative.js), takže se kreslí „N+". Vlastní index
// `total` má → jakmile přijde číslo, „+" zmizí bez zásahu do rendereru.

import { icon, iconHtml } from './icon.js';
import { CAT_ICONS, readMenu } from './menuModel.js';
import { CONTACT, phonesOn, telHref } from './headerTools.js';
import SHOP from '../../config/shop.js';
import { buildAdvice } from './categoryPage.js';
import { POPULAR, searchUrl } from './searchSuggest.js';
import { page, catalogRoot } from './pages.js';
import { NBSP, number } from './money.js';
import { plural } from '../locale/plural.js';
import L from '../locale/cs/searchRender.js';

// Texty → locale vrstva (S3 #399); dluh per soubor měří tools/lib/locale-debt.mjs
const LABEL_H1_PRE = L.h1Pre;
const LABEL_QUOTE_OPEN = L.quoteOpen;
const LABEL_QUOTE_CLOSE = L.quoteClose;
const LABEL_CHIPS = L.chipsLabel;
const LABEL_CHIPS_MORE = L.chipsMore;
const LABEL_CHIPS_LESS = L.chipsLess;
const LABEL_CAT_FOUND = L.catFound;
const LABEL_SHOW_ALL = L.showAll;
const LABEL_SHOWN = L.shown;
const LABEL_NONE_PRE = L.nonePre;
const LABEL_NONE_POST = L.nonePost;
const LABEL_NONE_TEXT = L.noneText;
// při vypnutých telefonech (#bs-config[data-bs-phones=off]) nesmí text slibovat hovor
const LABEL_NONE_TEXT_NO_PHONE = L.noneTextNoPhone;
const LABEL_NONE_PLACEHOLDER = L.nonePlaceholder;
const LABEL_SUBMIT = L.submit;
const LABEL_QUERIES = L.queries;
const LABEL_BROWSE = L.browse;
const LABEL_DID_YOU_MEAN = L.didYouMean;

const TIPS = L.tips;

// Poradna v prázdném stavu (mockup §isNone). Telefon i e-mail sdílí `CONTACT`
// s hlavičkou a patičkou, ať se čísla nerozejdou.
const ADVICE = {
  eyebrow: L.advice.eyebrow,
  title: L.advice.title,
  text: L.advice.text,
  textNoPhone: L.advice.textNoPhone,
  // kanály v pořadí chat → e-mail → telefon (buildAdvice, chatActions)
  actions: [
    { chat: 'search', primary: true },
    // `/kontakt/` — `/kontakty/` na shopu neexistuje (301), code review 2026-09-09
    { href: page('contact'), label: L.advice.mailLabel, icon: 'mail' },
    { href: telHref(CONTACT.phone), label: CONTACT.phone, icon: 'phone', primary: true },
  ],
};

// Dlaždice „Procházet kategorie" — jen kořeny, které v nativním menu opravdu jsou.
const BROWSE_CATS = [catalogRoot('garden'), catalogRoot('build'), catalogRoot('workshop'), catalogRoot('home'), page('sale')].filter(Boolean);

const PLURALS = L.plurals;

// --------------------------------------------------------------- utility

export function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

// „24 produktů" / „24+ produktů" (celek neznáme → `more`)
export function countLabel(n, kind, more) {
  return n + (more ? '+' : '') + ' ' + plural(more ? 5 : n, PLURALS[kind]);
}

function catIconName(href) {
  const path = (href || '').replace(/^https?:\/\/[^/]+/, '');
  return CAT_ICONS[path] || 'grid';
}

// --------------------------------------------------------------- položky skupin
//
// Každý builder umí obojí: obléct existující nativní `<a>` (fallback cesta ho
// předá) i postavit nový (API cesta ho vynechá). Proto `link` jako druhý
// argument — nativní uzel se MUTUJE, nikdy nenahrazuje, ať nepřijdeme
// o případné listenery default JS.

export function renderCatItem(data, link) {
  const node = link || el('a');
  node.href = data.url || '';
  node.className = 'bs-scat';
  node.textContent = '';
  const box = el('span', 'bs-scat__icon');
  // kategorie výsledku = štítek (mock M02), textové hledání = ikona kořene menu
  box.appendChild(icon(data.found ? 'tag' : catIconName(data.url)));
  node.appendChild(box);
  node.appendChild(el('span', 'bs-scat__name', data.name));
  // Počet zná JEN vlastní index. `found` (kategorie spočítané z nalezených
  // produktů, `categories_source: 'results'`) = počet SHOD s dotazem („45
  // nalezených"); jinak `products_count` obsahové kolekce = velikost
  // kategorie („14 produktů"). Nativní skupina počet nevydává, `data.count`
  // tam chybí a řádek se prostě nevykreslí, stejná karta, jen bez čísla.
  if (typeof data.count === 'number' && data.count > 0) {
    const label = data.found
      ? number(data.count) + NBSP + plural(data.count, LABEL_CAT_FOUND)
      : countLabel(data.count, 'products');
    node.appendChild(el('span', 'bs-scat__count', label));
  }
  node.insertAdjacentHTML('beforeend', iconHtml('arrow-right', 'bs-scat__arrow'));
  return node;
}

export function renderBrandItem(data, link) {
  const node = link || el('a');
  node.href = data.url || '';
  node.className = 'bs-sbrand';
  node.textContent = '';
  // wordmark je placeholder do dodání log (specka §Shoptet mapování)
  node.appendChild(el('span', 'bs-sbrand__mark', data.name));
  // Mockup má vedle boxu ještě jméno značky — jenže box je placeholder LOGA
  // a dokud loga nedorazí (#40), nese jméno on sám a druhý výskyt vedle by byl
  // „ONDRAGON ONDRAGON". Vypisuje se proto jen počet; jméno se přidá s logy.
  if (typeof data.count === 'number' && data.count > 0) {
    node.appendChild(el('span', 'bs-sbrand__count', countLabel(data.count, 'products')));
  } else {
    node.appendChild(el('span', 'bs-sbrand__spacer'));
  }
  node.insertAdjacentHTML('beforeend', iconHtml('arrow-right', 'bs-sbrand__arrow'));
  return node;
}

// Karta článku. Nativní výsledek nese jen titulek a URL — rubriku ani datum
// Shoptet ve skupině nevrací, takže je (dle zásady „žádná vymyšlená data")
// nekreslíme a obrázkové pole zůstává glyfem.
export function renderPostItem(data, link) {
  const node = link || el('a');
  node.href = data.url || '';
  node.className = 'bs-spost';
  node.textContent = '';
  const media = el('span', 'bs-spost__media');
  media.appendChild(icon('photo'));
  node.appendChild(media);
  const body = el('span', 'bs-spost__body');
  body.appendChild(el('span', 'bs-spost__title', data.name));
  node.appendChild(body);
  return node;
}

export function renderPageItem(data, link) {
  const node = link || el('a');
  const href = data.url || '';
  node.href = href;
  node.className = 'bs-spage';
  node.textContent = '';
  const box = el('span', 'bs-spage__icon');
  box.appendChild(icon('file'));
  node.appendChild(box);
  const body = el('span', 'bs-spage__body');
  body.appendChild(el('span', 'bs-spage__title', data.name));
  body.appendChild(el('span', 'bs-spage__url', SHOP.shop.domain + href.replace(/^https?:\/\/[^/]+/, '')));
  node.appendChild(body);
  return node;
}

export const ITEM_RENDERERS = {
  cats: renderCatItem,
  brands: renderBrandItem,
  posts: renderPostItem,
  pages: renderPageItem,
};

// --------------------------------------------------------------- skupiny

// Hlavička skupiny: `h4` + počet + linka (02-komponenty.md §5). `countAttr`
// označí počet jako živý (produkty se dopočítávají po loadMore).
export function dressGroupHead(head, count, countAttr) {
  if (!head || head.classList.contains('bs-sgroup__head')) return;
  head.classList.add('bs-sgroup__head');
  const badge = el('span', 'bs-sgroup__count');
  if (countAttr) badge.setAttribute('data-bs-products-count', '1');
  else badge.textContent = String(count);
  head.appendChild(badge);
  head.appendChild(el('span', 'bs-sgroup__line'));
}

// Obléknutí NATIVNÍ skupiny (fallback cesta): uzel i položky už v DOM jsou,
// dosazují se jen třídy a vnitřek odkazů.
export function dressGroup(node, section) {
  if (node.dataset.bsGroup) return;
  node.dataset.bsGroup = '1';
  node.classList.add('bs-sgroup', 'bs-sgroup--' + section.kind);

  dressGroupHead(node.querySelector('h4'), section.count);

  const list = node.querySelector('.search-results-group-list');
  if (list) list.classList.add('bs-sgroup__list');

  const render = ITEM_RENDERERS[section.kind];
  node.querySelectorAll('.search-results-group-list li > a').forEach(function (link) {
    render({ name: (link.textContent || '').replace(/\s+/g, ' ').trim(), url: link.getAttribute('href') }, link);
  });

  // „Zobrazit vše (N)" odkrývá `li.no-display` nativním JS — jen ho oblečeme
  const all = node.querySelector('.display-results-group');
  if (all) all.classList.add('bs-sgroup__all');
}

// Postavení skupiny OD NULY (cesta vlastního indexu, #67). Stejné třídy jako
// `dressGroup`, takže sedne táž SCSS.
export function renderGroup(section) {
  const node = el('section', 'bs-sgroup bs-sgroup--' + section.kind);
  node.id = section.id || '';
  node.dataset.bsGroup = '1';

  const head = el('h4', null, section.label);
  node.appendChild(head);
  dressGroupHead(head, section.count);

  // ⚠️ `bs-sgroup__list` je OBAL nad `ul`, ne `ul` samotné. Nativní markup má
  // `div.search-results-group-list > ul` a `dressGroup` výš třídu věší na ten
  // div — celá mřížka sekcí proto visí na `.bs-sgroup__list ul`. Když se tu
  // třída dala rovnou na `ul`, selektor nesedl a sekce Kategorie/Značky/Články
  // se vykreslily jako holý odrážkový seznam přes celou šířku (od #67 do
  // 2026-08-09). Struktura musí zrcadlit nativní, ne ji zkracovat.
  const list = el('div', 'bs-sgroup__list');
  const ul = el('ul');
  const render = ITEM_RENDERERS[section.kind] || renderPageItem;
  const items = section.items || [];
  items.forEach(function (item) {
    const li = el('li');
    li.appendChild(render(item));
    ul.appendChild(li);
  });
  list.appendChild(ul);
  node.appendChild(list);
  if (section.fold) foldGroup(node, items.length);
  return node;
}

// Sekce Kategorie z výsledků (mock M02): mobil ukáže prvních FOLD_MOBILE
// řádků, desktop (mřížka 3 sloupců) FOLD_DESKTOP = dva celé řádky; zbytek
// odkryje „Zobrazit vše (N)". Kolik je vidět, řídí CSS podle šířky
// (`bs-sgroup--fold-m` / `-d`), tlačítko jen přepne `is-open`.
const FOLD_MOBILE = 5;
const FOLD_DESKTOP = 6;

function foldGroup(node, count) {
  if (count <= FOLD_MOBILE) return;
  node.classList.add('bs-sgroup--fold-m');
  if (count > FOLD_DESKTOP) node.classList.add('bs-sgroup--fold-d');
  const all = el('button', 'bs-sgroup__all bs-sgroup__more', LABEL_SHOW_ALL(count));
  all.type = 'button';
  all.setAttribute('aria-expanded', 'false');
  all.addEventListener('click', function () {
    node.classList.add('is-open');
    all.setAttribute('aria-expanded', 'true');
  });
  node.appendChild(all);
}

/// --------------------------------------------------------------- hlavička
//
// Kompaktní hlavička (mock „Doporučeno" 2026-10-14, docs/design/
// 2026-10-14-vyhledavani-vysledky/, rámy D01/M01): jeden řádek H1 + počet
// produktů + slot řazení (staví searchPage.js), pod ním řada čipů kategorií
// výsledku. Eyebrow „Výsledky vyhledávání", souhrn po skupinách, poznámka
// o řazení i kotevní pilulky skupin zmizely (pod nimi byla první karta na
// iPhonu 13 až na y = 462 px). Na mobilu je předpona H1 jen pro čtečky.

export function renderHeading(model) {
  const head = el('div', 'bs-shead');
  const row = el('div', 'bs-shead__row');

  const h1 = el('h1', 'bs-shead__title');
  h1.appendChild(el('span', 'bs-shead__pre', LABEL_H1_PRE));
  h1.appendChild(el('span', 'bs-shead__query', quoted(model.query)));
  row.appendChild(h1);
  row.appendChild(el('span', 'bs-shead__total'));
  // řazení (taby / „Řadit: Shoda") dosadí searchPage.js, nativní cesta ho nemá
  row.appendChild(el('span', 'bs-shead__sort'));
  head.appendChild(row);

  const chips = renderChips(model.chips || []);
  if (chips) head.appendChild(chips);
  return head;
}

/** Dotaz v uvozovkách jazyka shopu („pila“). */
export function quoted(query) {
  return LABEL_QUOTE_OPEN + query + LABEL_QUOTE_CLOSE;
}

// --------------------------------------------------------------- čipy kategorií
//
// Kategorie výsledku z API (`categories_source: 'results'`, searchApiModel.js):
// čip = odkaz na stránku kategorie s počtem shod. Desktop drží jeden řádek
// a co se nevejde, schová „Další (N)" (vzor procházení výprodeje, saleBrowse.js
// fitChips); mobil = vodorovně posuvný pás bez „Další". Proklik měří
// searchAnalytics (`data-bs-kind` → result_type `category_chip`).

const CHIPS_MOBILE_MQ = '(max-width: 768px)';
let chipsMq = null;
function chipsMobile() {
  if (!chipsMq && window.matchMedia) chipsMq = window.matchMedia(CHIPS_MOBILE_MQ);
  return !!(chipsMq && chipsMq.matches);
}

function renderChips(list) {
  if (!list.length) return null;
  const box = el('nav', 'bs-schips');
  box.setAttribute('aria-label', LABEL_CHIPS);
  box.dataset.bsKind = 'cat_chip';
  // viditelný popisek = jméno navigace (aria-label), čtečka ho nečte dvakrát
  const label = el('span', 'bs-schips__label', LABEL_CHIPS);
  label.setAttribute('aria-hidden', 'true');
  box.appendChild(label);
  list.forEach(function (c) {
    const chip = el('a', 'bs-schip');
    chip.href = c.url || '';
    chip.appendChild(el('span', 'bs-schip__name', c.name));
    chip.appendChild(el('span', 'bs-schip__count', number(c.count)));
    box.appendChild(chip);
  });
  const more = el('button', 'bs-schip bs-schip--more');
  more.type = 'button';
  more.hidden = true;
  more.setAttribute('aria-expanded', 'false');
  more.appendChild(el('span', 'bs-schip__name'));
  more.appendChild(icon('chevron-down'));
  more.addEventListener('click', function () {
    box.classList.toggle('is-open');
    fitChips(box);
  });
  box.appendChild(more);
  return box;
}

/**
 * Desktop: čipy, které nejsou v prvním řádku, schová „Další (N)"; rozbalené
 * se zalamují a tlačítko říká „Méně". Měří až po vložení do dokumentu
 * (rAF / ResizeObserver, boot rozměry nečte).
 */
export function fitChips(box) {
  if (!box || !box.isConnected) return;
  const chips = Array.prototype.slice.call(box.querySelectorAll('.bs-schip:not(.bs-schip--more)'));
  const more = box.querySelector('.bs-schip--more');
  if (!chips.length || !more) return;
  chips.forEach(function (c) {
    c.hidden = false;
  });
  more.hidden = true;
  if (chipsMobile()) return; // pás se posouvá prstem, všechno je v něm
  const open = box.classList.contains('is-open');
  const top = chips[0].offsetTop;
  const overflow = function () {
    return chips.filter(function (c) {
      return !c.hidden && c.offsetTop > top;
    });
  };
  if (!overflow().length) {
    box.classList.remove('is-open');
    return;
  }
  more.hidden = false;
  if (open) {
    setMore(more, true, 0);
    return;
  }
  overflow().forEach(function (c) {
    c.hidden = true;
  });
  const hiddenCount = function () {
    return chips.filter(function (c) {
      return c.hidden;
    }).length;
  };
  setMore(more, false, hiddenCount());
  // „Další (N)" musí zůstat v prvním řádku, ubírá se od konce, dokud se nevejde
  for (let i = chips.length - 1; i > 0 && more.offsetTop > top; i--) {
    if (chips[i].hidden) continue;
    chips[i].hidden = true;
    setMore(more, false, hiddenCount());
  }
}

function setMore(more, open, hiddenCount) {
  more.querySelector('.bs-schip__name').textContent = open ? LABEL_CHIPS_LESS : LABEL_CHIPS_MORE(hiddenCount);
  more.setAttribute('aria-expanded', String(open));
  more.classList.toggle('is-open', open);
}

/** Přepočet řádku čipů při změně šířky (jeden observer na řadu). */
export function watchChips(box) {
  if (!box) return;
  requestAnimationFrame(function () {
    fitChips(box);
  });
  if (typeof ResizeObserver === 'undefined') return;
  let width = 0;
  new ResizeObserver(function (entries) {
    const w = Math.round(entries[0].contentRect.width);
    if (w === width) return;
    width = w;
    fitChips(box);
  }).observe(box);
}

/** „146 produktů" (celek neznáme → „24+ produktů"). */
function totalLabel(products) {
  const more = products.total === null && products.hasMore;
  const n = products.total === null ? products.shown : products.total;
  return number(n) + (more ? '+' : '') + NBSP + plural(more ? 5 : n, PLURALS.products);
}

// Počet produktů v hlavičce, přeřazení a loadMore ho jen dorovnávají.
export function updateTotals(model) {
  const out = document.querySelector('.bs-shead__total');
  if (!out) return;
  const p = model.products;
  const n = p.total === null ? p.shown : p.total;
  out.textContent = n ? totalLabel(p) : '';
}

// Živé počty produktů (pilulka, hlavička sekce, počítadlo pod mřížkou).
export function updateProductCounts(products) {
  const more = products.total === null && products.hasMore;
  document.querySelectorAll('[data-bs-products-count]').forEach(function (node) {
    node.textContent = (products.total === null ? products.shown : products.total) + (more ? '+' : '');
  });
  // mock: „Zobrazeno 12 z 24 produktů" — „z celku" jen když celek známe (API
  // cesta); nativní fallback celek nemá (total === null) a zůstává u kratší formy
  document.querySelectorAll('.bs-sprod__shown').forEach(function (node) {
    node.textContent =
      LABEL_SHOWN +
      (products.total !== null && products.total > products.shown
        ? products.shown + ' z ' + countLabel(products.total, 'products')
        : countLabel(products.shown, 'products'));
  });
  // červený progress pruh u loadMore (mock: width = shown/total)
  document.querySelectorAll('.bs-smore__progress > span').forEach(function (fill) {
    if (products.total) fill.style.width = Math.round((products.shown / products.total) * 100) + '%';
  });
}

// --------------------------------------------------------------- prázdný stav

function sectionHead(label) {
  const head = el('h2', 'bs-sgroup__head bs-sgroup__head--plain', label);
  head.appendChild(el('span', 'bs-sgroup__line'));
  return head;
}

// Sdílené vyhledávací pole `.bs-sfield` (desktop 58px pilulka s tmavým
// tlačítkem, mobil 52px šedý pilík s kulatým tlačítkem). GET na /vyhledavani/
// → funguje i bez JS, jakmile je pole v DOM. Používá ho i 404 (#33).
export function buildSearchForm(placeholder) {
  const form = el('form', 'bs-sfield');
  form.action = page('search');
  form.method = 'get';
  form.appendChild(icon('search', 'bs-sfield__icon'));
  const input = el('input', 'bs-sfield__input');
  input.type = 'search';
  input.name = 'string';
  input.placeholder = placeholder;
  input.setAttribute('aria-label', LABEL_SUBMIT);
  form.appendChild(input);
  const submit = el('button', 'bs-sfield__submit');
  submit.type = 'submit';
  submit.appendChild(el('span', 'bs-sfield__submit-label', LABEL_SUBMIT));
  submit.insertAdjacentHTML('beforeend', iconHtml('search', 'bs-sfield__submit-icon'));
  form.appendChild(submit);
  return form;
}

function buildQueries() {
  const box = el('section', 'bs-sempty__queries');
  box.appendChild(sectionHead(LABEL_QUERIES));
  const chips = el('div', 'bs-sempty__chips');
  POPULAR.forEach(function (q) {
    const chip = el('a', 'bs-sempty__chip');
    chip.href = searchUrl(q);
    chip.appendChild(icon('search'));
    chip.appendChild(el('span', null, q));
    chips.appendChild(chip);
  });
  box.appendChild(chips);
  return box;
}

function buildBrowse() {
  const menu = readMenu();
  if (!menu.length) return null;
  const byPath = {};
  menu.forEach(function (r) {
    byPath[r.path] = r;
  });
  const cats = BROWSE_CATS.map(function (p) {
    return byPath[p];
  }).filter(Boolean);
  if (!cats.length) return null;

  const box = el('section', 'bs-sempty__cats');
  box.appendChild(sectionHead(LABEL_BROWSE));
  const grid = el('div', 'bs-sempty__tiles');
  cats.forEach(function (cat) {
    const tile = el('a', 'bs-sempty__tile');
    tile.href = cat.href;
    const circle = el('span', 'bs-sempty__tile-icon');
    circle.appendChild(icon(CAT_ICONS[cat.path] || 'grid'));
    tile.appendChild(circle);
    tile.appendChild(el('span', 'bs-sempty__tile-label', cat.label));
    grid.appendChild(tile);
  });
  box.appendChild(grid);
  return box;
}

/**
 * @param {string} query dotaz, na který nic nesedlo
 * @param {string} [suggestion] opravený dotaz z indexu (`did_you_mean`) —
 *   když přijde, nahradí tip o překlepech nabídkou „Mysleli jste: …".
 *   Oprava faktického tvrzení z mockupu (#67): náš index JE typo-tolerantní,
 *   takže rada „vyhledávání je citlivé na překlepy" by lhala.
 */
export function renderEmpty(query, suggestion) {
  const wrap = el('div', 'bs-sempty');

  const main = el('div', 'bs-sempty__main');
  const circle = el('span', 'bs-sempty__badge');
  circle.appendChild(icon('search'));
  main.appendChild(circle);

  const h1 = el('h1', 'bs-sempty__title');
  h1.appendChild(document.createTextNode(LABEL_NONE_PRE));
  h1.appendChild(el('span', 'bs-shead__query', quoted(query)));
  h1.appendChild(document.createTextNode(LABEL_NONE_POST));
  main.appendChild(h1);

  if (suggestion) {
    const did = el('p', 'bs-sempty__did');
    did.appendChild(document.createTextNode(LABEL_DID_YOU_MEAN));
    const link = el('a', 'bs-sempty__did-link', suggestion);
    link.href = searchUrl(suggestion);
    did.appendChild(link);
    main.appendChild(did);
  }

  main.appendChild(el('p', 'bs-sempty__text', phonesOn() ? LABEL_NONE_TEXT : LABEL_NONE_TEXT_NO_PHONE));
  main.appendChild(buildSearchForm(LABEL_NONE_PLACEHOLDER));
  wrap.appendChild(main);

  const tips = el('div', 'bs-sempty__tips');
  // s návrhem z indexu je rada o překlepech zbytečná — index je typo-tolerantní
  const list = suggestion ? TIPS.filter((t, i) => i !== L.typoTipIndex) : TIPS;
  list.forEach(function (t) {
    const row = el('div', 'bs-sempty__tip');
    const mark = el('span', 'bs-sempty__tip-mark');
    mark.appendChild(icon('check'));
    row.appendChild(mark);
    row.appendChild(el('span', null, t));
    tips.appendChild(row);
  });
  wrap.appendChild(tips);

  const advice = buildAdvice(ADVICE);
  advice.classList.add('bs-advice--search');
  wrap.appendChild(advice);

  wrap.appendChild(buildQueries());
  const browse = buildBrowse();
  if (browse) wrap.appendChild(browse);

  return wrap;
}
